/**
 * Was im Steckbrief eines Linebound steht (15.10.2026).
 *
 * Seit die Auswahl Portraets zeigt, steht auf der Kachel nur noch Name und
 * Rolle. Vor der Wahl soll man trotzdem nachlesen koennen, was die Figur
 * kann - das sammelt diese Datei zusammen.
 *
 * SIE ERFINDET NICHTS. Jede Zeile kommt aus den Spieldaten: Namen und
 * Beschreibungen der Faehigkeiten aus SKILLS, die Ausbauwege aus dem
 * Skilltree, Leben und Ressource aus HEROES. Was hier nicht steht, steht
 * auch im Spiel nicht - und was sich dort aendert, aendert sich hier mit.
 *
 * Nur zwei Dinge kommen aus der Textdatei: die Beschriftungen und die
 * Passive, denn die hat keine eigenen Daten - sie ist in der Simulation
 * fest eingebaut und wird im HUD aus denselben Texten beschrieben.
 *
 * Eine eigene Funktion und kein Bauteil, weil sich so OHNE Bild pruefen
 * laesst, dass jeder spielbare Linebound einen vollstaendigen Steckbrief hat.
 */

import {
  getCapstones,
  getHero,
  getSkillUpgrades,
  getSkillsForHero,
  getTalents,
  isPlayableHero,
  type HeroDefinition,
} from '@dueline/game-data';
import { t } from '../i18n/index.js';

/** Eine Faehigkeit, wie sie im Steckbrief steht. */
export interface SteckbriefFaehigkeit {
  /** 1 bis 3 sind die Grundskills, 4 ist das Ultimate. */
  slot: 1 | 2 | 3 | 4;
  name: string;
  beschreibung: string;
  /** Die Ausbauwege dieses Skills. Das Ultimate hat keine. */
  ausbau: { name: string; beschreibung: string }[];
}

export interface Steckbrief {
  hero: HeroDefinition;
  /** Der Name der Figur aus der Lore - gross auf der Kachel. */
  linebound: string;
  /** Der Archetyp: Vanguard, Arcanist, Deadeye, Shade. */
  rolle: string;
  /** Ein Satz zur Figur. */
  satz: string;
  /** Was die Rolle im Spiel tut, in einer Zeile. */
  rollenSatz: string;
  passive: { name: string; beschreibung: string } | null;
  faehigkeiten: SteckbriefFaehigkeit[];
  /** Die Waehlbaren auf Level 8 und 10. */
  wahlen: { name: string; beschreibung: string }[];
  /** Leben, Ressource und Tempo - die Zahlen, die man vergleicht. */
  werte: { label: string; wert: string }[];
}

/**
 * Die Passive eines Linebound.
 *
 * Sie steht nicht in den Spieldaten: Jede ist in der Simulation eigens
 * gebaut (Sap Surge, Marked Prey, Momentum, Stand Your Ground). Beschrieben
 * wird sie deshalb mit denselben Texten wie im HUD - eine Quelle fuer beide
 * Stellen.
 */
function passiveFuer(heroId: string): { name: string; beschreibung: string } | null {
  switch (heroId) {
    case 'vanguard':
      return { name: t.standYourGround.label, beschreibung: t.standYourGround.hint };
    case 'arcanist':
      return { name: t.overflow.label, beschreibung: t.overflow.hint };
    case 'deadeye':
      return { name: t.markedPrey.label, beschreibung: t.markedPrey.hint };
    case 'shade':
      return { name: t.momentum.label, beschreibung: t.momentum.hint };
    default:
      return null;
  }
}

/**
 * Stellt den Steckbrief zusammen - oder null, wenn es den Linebound nicht
 * gibt oder er noch nicht spielbar ist.
 */
export function baueSteckbrief(heroId: string): Steckbrief | null {
  const hero = getHero(heroId);
  if (!hero || !isPlayableHero(heroId)) return null;

  const faehigkeiten: SteckbriefFaehigkeit[] = getSkillsForHero(heroId).map((skill) => ({
    slot: skill.slot,
    name: skill.name,
    beschreibung: skill.description,
    /*
     * Das Ultimate hat keine Ausbauwege (Skilltree, Abschnitt 46). Die
     * Abfrage steht trotzdem fuer jeden Slot hier und nicht als Sonderfall
     * daneben: Was es nicht gibt, kommt als leere Liste zurueck.
     */
    ausbau:
      skill.slot === 4
        ? []
        : getSkillUpgrades(heroId, skill.slot).map((ausbau) => ({
            name: ausbau.name,
            beschreibung: ausbau.description,
          })),
  }));

  const wahlen = [...getTalents(heroId), ...getCapstones(heroId)].map((wahl) => ({
    name: wahl.name,
    beschreibung: wahl.description,
  }));

  return {
    hero,
    linebound: hero.linebound,
    rolle: hero.name,
    satz: hero.pitch,
    rollenSatz: t.menu.heroRoles[hero.role],
    passive: passiveFuer(heroId),
    faehigkeiten,
    wahlen,
    werte: [
      { label: t.steckbrief.health, wert: String(hero.maxHp) },
      {
        label: hero.resource === 'mana' ? t.steckbrief.mana : t.steckbrief.energy,
        wert: String(hero.maxResource),
      },
      { label: t.steckbrief.moveSpeed, wert: String(hero.moveSpeed) },
    ],
  };
}
